import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { APP_CONFIG } from '@boma/app-config';
import { NotificationsModule } from '@boma/libs/shared/modules';
import { AuthGuard, HeaderInterceptor } from '@boma/shared/common';
import * as fromSharedState from '@boma/supplier-portal/shared/state';
import {
  clearState,
  LocationsFacade,
  TillStatusEffects,
  UiStyleEffects,
  UserEffects,
} from '@boma/supplier-portal/shared/state';
import { permissionsFeatureKey } from '@boma/supplier-portal/users';
import { UiModule } from '@boma/ui';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { ActionReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OAuthModule } from 'angular-oauth2-oidc';
import player from 'lottie-web';
import { localStorageSync } from 'ngrx-store-localstorage';
import { LottieModule } from 'ngx-lottie';
import { RippleModule } from 'primeng/ripple';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';

export function localStorageSyncReducer(
  reducer: ActionReducer<any>
): ActionReducer<any> {
  return localStorageSync({
    keys: [
      fromSharedState.UISTYLE_FEATURE_KEY,
      { [permissionsFeatureKey]: ['entities'] },
    ],
    rehydrate: true,
  })(reducer);
}

export function playerFactory() {
  return player;
}

const routingModule = RouterModule.forRoot(
  [
    {
      path: '',
      loadChildren: () =>
        import('@boma/supplier-portal/dashboard').then(
          (module) => module.SupplierPortalDashboardModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'dashboard',
      loadChildren: () =>
        import('@boma/supplier-portal/dashboard').then(
          (module) => module.SupplierPortalDashboardModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'sales',
      loadChildren: () =>
        import('@boma/supplier-portal/sales').then(
          (module) => module.SupplierPortalSalesModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'transfers',
      loadChildren: () =>
        import('@boma/supplier-portal/transfers').then(
          (module) => module.SupplierPortalTransfersModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'maps',
      loadChildren: () =>
        import('@boma/supplier-portal/maps').then(
          (module) => module.SupplierPortalMapsModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'reports',
      loadChildren: () =>
        import('@boma/supplier-portal/reports').then(
          (module) => module.SupplierPortalReportsModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'admin',
      loadChildren: () =>
        import('@boma/supplier-portal/administration').then(
          (module) => module.SupplierPortalAdministrationModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'orders',
      loadChildren: () =>
        import('@boma/supplier-portal/orders').then(
          (module) => module.OrdersModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'integrations/payments',
      loadChildren: () =>
        import('@boma/supplier-portal/payments').then(
          (module) => module.SupplierPortalPaymentsModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'pricing',
      loadChildren: () =>
        import('@boma/supplier-portal/pricing').then(
          (module) => module.PricingModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'report-sales',
      loadChildren: () =>
        import('@boma/supplier-portal/report-sales').then(
          (module) => module.SupplierPortalReportSalesModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'report-stock',
      loadChildren: () =>
        import('@boma/supplier-portal/report-stock').then(
          (module) => module.SupplierPortalReportStockModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'catalogue',
      loadChildren: () =>
        import('@boma/supplier-portal/catalogue').then(
          (module) => module.CatalogueModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'locations',
      loadChildren: () =>
        import('@boma/supplier-portal/locations').then(
          (module) => module.LocationsModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'stock',
      loadChildren: () =>
        import('@boma/supplier-portal/inventory').then(
          (module) => module.InventoryModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'company-information',
      loadChildren: () =>
        import('@boma/supplier-portal/organization').then(
          (module) => module.CompanyInformationModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'users',
      loadChildren: () =>
        import('@boma/supplier-portal/users').then(
          (module) => module.SupplierPortalUsersModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'roles',
      loadChildren: () =>
        import('@boma/supplier-portal/users').then(
          (module) => module.SupplierPortalRolesModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'inbox',
      loadChildren: () =>
        import('@boma/supplier-portal/inbox').then(
          (module) => module.SupplierPortalInboxModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'my-account',
      loadChildren: () =>
        import('@boma/supplier-portal/my-account').then(
          (module) => module.SupplierPortalMyAccountModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'invoices',
      loadChildren: () =>
        import('@boma/supplier-portal/invoices').then(
          (module) => module.SupplierPortalInvoicesModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'customers',
      loadChildren: () =>
        import('@boma/supplier-portal/customers').then(
          (module) => module.SupplierPortalCustomersModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'listings',
      loadChildren: () =>
        import('@boma/supplier-portal/listings').then(
          (module) => module.SupplierPortalListingsModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'feed-item',
      loadChildren: () =>
        import('@boma/supplier-portal/feed-item').then(
          (module) => module.SupplierPortalFeedItemModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'messaging',
      loadChildren: () =>
        import('@boma/supplier-portal/chat-room').then(
          (module) => module.SupplierPortalChatRoomModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'calendar',
      loadChildren: () =>
        import('@boma/supplier-portal/calender').then(
          (module) => module.SupplierPortalCalenderModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'settings',
      loadChildren: () =>
        import('@boma/supplier-portal/settings').then(
          (module) => module.SupplierPortalSettingsModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'account',
      loadChildren: () =>
        import('@boma/supplier-portal/account').then(
          (module) => module.SupplierPortalAccountModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'accounting',
      loadChildren: () =>
        import('@boma/supplier-portal/accounting').then(
          (module) => module.SupplierPortalAccountingModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'vehicles',
      loadChildren: () =>
        import('@boma/supplier-portal/vehicles').then(
          (module) => module.VehiclesModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'bank',
      loadChildren: () =>
        import('@boma/supplier-portal/bank').then(
          (module) => module.SupplierPortalBankModule
        ),
      canActivate: [AuthGuard],
    },
    {
      path: 'help',
      loadChildren: () =>
        import('@boma/supplier-portal/help').then(
          (module) => module.SupplierPortalHelpModule
        ),
    },
    //TODO MAKE A LOGIN COMPONENT TO REDIRECT ACCORDINGLY
    {
      path: 'login',
      loadChildren: () =>
        import('@boma/supplier-portal/login').then(
          (module) => module.SupplierPortalLoginModule
        ),
    },
    { path: '**', redirectTo: 'login' },
  ],
  {
    initialNavigation: 'enabledNonBlocking',
    scrollPositionRestoration: 'top',
  }
);

@NgModule({
  declarations: [AppComponent],
  imports: [
    provideFirebaseApp(() => {
      return initializeApp(environment.firebaseConfig);
    }),
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    UiModule,
    RippleModule,
    routingModule,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.apiRoute],
        sendAccessToken: true,
      },
    }),
    StoreModule.forRoot(
      {
        [fromSharedState.UISTYLE_FEATURE_KEY]: fromSharedState.reducer,
        [fromSharedState.USER_FEATURE_KEY]: fromSharedState.userReducer,
        [fromSharedState.TILL_STATE_FEATURE_KEY]:
          fromSharedState.createTillStatusReducer,
      },
      {
        metaReducers: [localStorageSyncReducer, clearState],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      }
    ),
    EffectsModule.forRoot([UiStyleEffects, UserEffects, TillStatusEffects]),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    StoreRouterConnectingModule.forRoot(),
    LottieModule.forRoot({ player: playerFactory }),
    RippleModule,
    FormsModule,
    NotificationsModule,
  ],
  providers: [
    { provide: APP_CONFIG, useValue: environment },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderInterceptor,
      multi: true,
    },
    LocationsFacade,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
